import { Component, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, of, startWith, switchMap } from 'rxjs';
import {
  LEGAL_PAGE_TRANSLATION_KEYS,
  LegalPage,
  LegalPageSlug,
} from './shared/legal-pages.data';

/** Iconsax icon names cycling through for each numbered section */
const SECTION_ICONS: string[] = [
  'tick-circle',
  'user-1',
  'shield-tick',
  'document-text-1',
  'box',
  'lock-1',
  'warning-triangle',
  'globe',
  'message-text',
  'award',
  'info-circle',
  'calendar-1',
];

@Component({
  selector: 'pwj-legal-page',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './legal-page.component.html',
  styleUrl: './legal-page.component.scss',
})
export class LegalPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly page = toSignal(
    this.route.data.pipe(
      map(d => d['slug'] as LegalPageSlug),
      switchMap(slug => {
        if (!slug) return of(null);
        const translationKey = LEGAL_PAGE_TRANSLATION_KEYS[slug];
        return this.translate.onLangChange.pipe(
          startWith({ lang: this.translate.currentLang, translations: {} }),
          switchMap(() => this.translate.get(translationKey)),
          map(content => (content as LegalPage) ?? null)
        );
      })
    ),
    { initialValue: null }
  );

  /** Returns an iconsax icon name for numbered sections (cycles through predefined list) */
  getSectionIcon(index: number): string {
    return SECTION_ICONS[index % SECTION_ICONS.length];
  }

  /** Smooth-scroll to a section — browser-only, safe for SSR */
  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    if (!isPlatformBrowser(this.platformId)) return;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
